import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { useCan } from '../../api/me';
import { ErrorAlert } from '../../components/error-alert';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { isDefaultComplete } from './labels';
import { useMarketDefaults, useOwners, type MarketDefault } from './queries';
import { RuleEditor } from './rule-editor';
import { OwnerChips } from './rules-table';

/** Anchor of the card: the Webhook URLs card links here when a channel cannot go live. */
export const MARKET_DEFAULTS_ANCHOR = 'lead-market-defaults';

/**
 * Card "Market defaults": the CZ and Spain rules that take every lead without an active campaign
 * rule. Edit opens the rules tab's editor in market-default mode, above the card.
 */
export function DefaultsCard() {
  const { t } = useTranslation(['leads', 'common']);
  const canManage = useCan()('leads.manage');
  const defaults = useMarketDefaults();
  const owners = useOwners();
  const activeIds = owners.data ? new Set(owners.data.map((o) => o.pdId)) : null;
  const [editing, setEditing] = useState<MarketDefault | null>(null);
  const [closedStale, setClosedStale] = useState(false);
  const opener = useRef<HTMLElement | null>(null);

  const open = (rule: MarketDefault) => {
    opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setClosedStale(false);
    setEditing(rule);
  };
  const close = () => {
    setEditing(null);
    const back = opener.current;
    requestAnimationFrame(() => {
      if (back?.isConnected) back.focus();
    });
  };

  // Saved elsewhere while the editor was open: close rather than let Save write stale values.
  const current = editing ? defaults.data?.find((d) => d.id === editing.id) : undefined;
  if (editing && current && current.version !== editing.version) {
    setEditing(null);
    setClosedStale(true);
  }

  const rows = defaults.data ?? [];
  return (
    <>
      {editing && <RuleEditor key={editing.id} target={{ kind: 'default', rule: editing }} onClose={close} />}
      {closedStale && (
        <p className="bo-notice" role="status">
          {t('general.defaults.editorClosedStale')}
        </p>
      )}
      <section className="hp-card" id={MARKET_DEFAULTS_ANCHOR} tabIndex={-1}>
        <HpCardHeader title={t('general.defaults.title')} subtitle={t('general.defaults.subtitle')} icon="globe" />
        <ErrorAlert error={defaults.error ?? owners.error} className="bo-alert--inset" />
        {defaults.isLoading ? (
          <p className="hp-card__body">
            <Spinner>{t('common:actions.loading')}</Spinner>
          </p>
        ) : (
          <TableScroll label={t('general.defaults.title')}>
            <BrutTable>
              <BrutTheadRow>
                <th>{t('general.defaults.columns.market')}</th>
                <th>{t('rules.columns.owners')}</th>
                <th>{t('rules.columns.target')}</th>
                <th>{t('rules.columns.exceptions')}</th>
                <th>{t('rules.columns.status')}</th>
                {canManage && <th>{t('rules.columns.actions')}</th>}
              </BrutTheadRow>
              <tbody>
                {rows.map((rule) => {
                  const complete = isDefaultComplete(rule, activeIds);
                  return (
                    <tr key={rule.id}>
                      <td>
                        <span className="bo-strong">{rule.marketLabel ?? rule.marketCode}</span>
                        <br />
                        <small className="bo-muted bo-mono">{rule.marketCode}</small>
                      </td>
                      <td>
                        {rule.owners.length > 0 ? (
                          <OwnerChips rule={rule} activeIds={activeIds} />
                        ) : (
                          <span className="bo-muted">{t('general.defaults.noOwners')}</span>
                        )}
                      </td>
                      <td>
                        {rule.pipeline ? (
                          <span>
                            {rule.pipeline.name ?? t('rules.unknownId', { id: rule.pipeline.pdId })}
                            {' → '}
                            {rule.stage ? (
                              (rule.stage.name ?? t('rules.unknownId', { id: rule.stage.pdId }))
                            ) : (
                              <span className="bo-muted">{t('general.defaults.noStage')}</span>
                            )}
                          </span>
                        ) : (
                          <span className="bo-muted">{t('general.defaults.noTarget')}</span>
                        )}
                      </td>
                      <td>
                        {rule.respectExistingOwner ? (
                          <HpTag tone="info" title={t('rules.existingOwnerTitle')}>
                            {t('rules.existingOwner')}
                          </HpTag>
                        ) : (
                          <span className="bo-muted">—</span>
                        )}
                      </td>
                      <td>
                        <HpTag
                          tone={complete ? 'success' : 'warning'}
                          title={complete ? undefined : t('general.defaults.incompleteTitle')}
                        >
                          {t(complete ? 'general.defaults.complete' : 'general.defaults.incomplete')}
                        </HpTag>
                      </td>
                      {canManage && (
                        <td>
                          <HpButton
                            variant="quiet"
                            icon="edit"
                            className="bo-button--sm"
                            disabled={editing !== null}
                            onClick={() => open(rule)}
                          >
                            {t('common:actions.edit')}
                          </HpButton>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </BrutTable>
          </TableScroll>
        )}
        <div className="hp-card__body bo-sections">
          <p className="bo-hint">{t('general.defaults.hint')}</p>
        </div>
      </section>
    </>
  );
}
